import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Spinner } from '../../components/ui/Spinner';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { createEtudiant, fetchEtudiantById, updateEtudiant, clearCurrentEtudiant } from '../../store/slices/etudiantSlice';
import { Etudiant, useGetChoixFormulaireQuery } from '../../services/etudiantsApi';
import { useGetPolesQuery } from '../../services/polesOptionsApi';
import { useGetClassesQuery } from '../../services/polesOptionsApi';
import { Upload, X, ArrowLeft, Save, User, GraduationCap, Phone, Camera } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { AlertBanner } from '../../components/ui/AlertBanner';
import PhotoCropModal from '../../components/Photo/PhotoCropModal';

export const EtudiantForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { currentEtudiant, loading } = useAppSelector((s) => s.etudiants);
  const { data: choixFormulaire } = useGetChoixFormulaireQuery();
  const { data: polesBDD = [] } = useGetPolesQuery({ actif: true });
  const { data: classesBDD = [] } = useGetClassesQuery({ actif: true });

  const [form, setForm] = useState({
    matricule: '',
    nom: '',
    prenoms: '',
    sexe: '' as 'M' | 'F' | '',
    date_naissance: '',
    lieu_naissance: '',
    niveau: 'L1' as 'L1' | 'L2' | 'L3' | 'M1' | 'M2',
    type_formation: 'FI' as 'FI' | 'CS' | 'DD',
    parcours: '',
    option: '',
    pole: '',
    classe: '',
    email: '',
    telephone: '',
    actif: true,
  });
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [photoRemoved, setPhotoRemoved] = useState(false);
  // Source brute pour la modal de recadrage (dataURL de l'image avant crop)
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [cropFilename, setCropFilename] = useState<string>('photo.jpg');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const photoInputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (id) {
      dispatch(fetchEtudiantById(parseInt(id)));
    } else {
      dispatch(clearCurrentEtudiant());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (currentEtudiant) {
      setForm({
        matricule: currentEtudiant.matricule || '',
        nom: currentEtudiant.nom || '',
        prenoms: currentEtudiant.prenoms || (currentEtudiant as any).prenom || '',
        sexe: (currentEtudiant.sexe as 'M' | 'F' | '') || '',
        date_naissance: currentEtudiant.date_naissance || '',
        lieu_naissance: currentEtudiant.lieu_naissance || '',
        niveau: (currentEtudiant.niveau as 'L1' | 'L2' | 'L3' | 'M1' | 'M2') || 'L1',
        type_formation: ((currentEtudiant as any).type_formation as 'FI' | 'CS' | 'DD') || 'FI',
        parcours: (currentEtudiant as any).parcours || '',
        option: currentEtudiant.option || '',
        pole: currentEtudiant.pole || '',
        classe: (currentEtudiant as any).classe || '',
        email: currentEtudiant.email || '',
        telephone: currentEtudiant.telephone || '',
        actif: currentEtudiant.actif ?? true,
      });
      if (currentEtudiant.photo_url) {
        setPhotoPreview(currentEtudiant.photo_url);
      } else if (currentEtudiant.photo_path) {
        setPhotoPreview(`${window.location.hostname === 'localhost' ? 'http://localhost:8000' : ''}/media/${currentEtudiant.photo_path}`);
      }
    }
  }, [currentEtudiant]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const newValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;

    // Si la classe est changée, auto-remplir niveau, option, pôle, parcours
    if (name === 'classe' && value) {
      const classeObj = classesBDD.find((c: any) => c.code === value);
      if (classeObj) {
        setForm((f) => ({
          ...f,
          classe: value,
          niveau: (classeObj.niveau || f.niveau) as typeof f.niveau,
          option: classeObj.option_nom || f.option,
          pole: classeObj.pole_nom || f.pole,
          parcours: classeObj.parcours_nom || f.parcours,
        }));
        return;
      }
    }

    setForm((f) => ({ ...f, [name]: newValue }));
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Veuillez sélectionner un fichier image');
      if (photoInputRef.current) photoInputRef.current.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("La taille de l'image ne doit pas dépasser 5MB");
      if (photoInputRef.current) photoInputRef.current.value = '';
      return;
    }
    // Lecture du fichier en dataURL puis ouverture de la modal de recadrage
    const reader = new FileReader();
    reader.onloadend = () => {
      setCropSrc(reader.result as string);
      setCropFilename(file.name);
    };
    reader.readAsDataURL(file);
    // Reset l'input pour permettre de re-sélectionner le même fichier
    if (photoInputRef.current) photoInputRef.current.value = '';
  };

  const handleCropValidate = async (croppedFile: File) => {
    // Compression : photo de profil → 400 Ko max, 800px de côté (avatar)
    const { compressImage } = await import('../../utils/imageCompression');
    const { file } = await compressImage(croppedFile, {
      maxSizeMB: 0.4,
      maxWidthOrHeight: 800,
      initialQuality: 0.85,
    });
    setPhotoFile(file);
    setPhotoRemoved(false);
    const reader = new FileReader();
    reader.onloadend = () => setPhotoPreview(reader.result as string);
    reader.readAsDataURL(file);
    setCropSrc(null);
  };

  const handleRemovePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    setPhotoRemoved(true);
    if (photoInputRef.current) photoInputRef.current.value = '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaving(true);

    try {
      const needsFormData = !!photoFile || photoRemoved;
      const dataToSend: Record<string, any> = {
        matricule: form.matricule || undefined,
        nom: form.nom || undefined,
        prenoms: form.prenoms || undefined,
        sexe: form.sexe || undefined,
        date_naissance: form.date_naissance || undefined,
        lieu_naissance: form.lieu_naissance || undefined,
        niveau: form.niveau,
        type_formation: form.type_formation || undefined,
        parcours: form.parcours || undefined,
        option: form.option || undefined,
        pole: form.pole || undefined,
        classe: form.classe || undefined,
        email: form.email || undefined,
        telephone: form.telephone || undefined,
        actif: form.actif,
      };

      let payload: FormData | Record<string, any>;

      if (needsFormData) {
        const formData = new FormData();
        Object.entries(dataToSend).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            formData.append(key, value.toString());
          }
        });
        if (photoFile) {
          formData.append('photo', photoFile);
        } else if (photoRemoved) {
          formData.append('photo_path', '');
        }
        payload = formData;
      } else {
        payload = dataToSend;
      }

      if (id) {
        await dispatch(updateEtudiant({ id: parseInt(id), data: payload })).unwrap();
        navigate(`/etudiants/${id}`);
      } else {
        const res = await dispatch(createEtudiant(payload)).unwrap();
        navigate(`/etudiants/${res.id}`);
      }
    } catch (err: any) {
      console.error('Erreur:', err);
      if (typeof err === 'object' && err !== null) {
        const messages = Object.entries(err)
          .map(([key, val]) => `${key}: ${Array.isArray(val) ? val.join(', ') : val}`)
          .join(' | ');
        setError(messages || 'Erreur lors de la sauvegarde');
      } else {
        setError('Erreur lors de la sauvegarde');
      }
      setSaving(false);
    }
  };

  if (loading && id) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  const selectClasses = "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100";

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header (unifié — refonte vague 3 lot 1) */}
      <PageHeader
        title={id ? "Modifier l'étudiant" : 'Nouvel étudiant'}
        description={id && currentEtudiant ? (
          <><span className="font-mono">{currentEtudiant.matricule}</span> — {currentEtudiant.nom} {currentEtudiant.prenoms}</>
        ) : 'Renseignez les informations puis enregistrez.'}
        back={
          <Button variant="outline" size="sm" icon={<ArrowLeft size={14} />}
            onClick={() => navigate('/etudiants')}>
            Retour
          </Button>
        }
      />

      {/* Error */}
      {error && (
        <AlertBanner type="error" message={error} onClose={() => setError(null)} />
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Identité */}
        <Card>
          <div className="flex items-center gap-2 pb-3 mb-4 border-b border-gray-100 dark:border-gray-700">
            <User size={18} className="text-blue-600" />
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">Identité</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Matricule <span className="text-red-500">*</span></label>
              <Input name="matricule" value={form.matricule} onChange={handleChange} required
                placeholder="Ex: 2024L1001" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Sexe</label>
              <select name="sexe" value={form.sexe} onChange={handleChange} className={selectClasses}>
                <option value="">--</option>
                <option value="M">Masculin</option>
                <option value="F">Féminin</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nom <span className="text-red-500">*</span></label>
              <Input name="nom" value={form.nom} onChange={handleChange} required placeholder="Nom de famille" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Prénoms <span className="text-red-500">*</span></label>
              <Input name="prenoms" value={form.prenoms} onChange={handleChange} required placeholder="Prénoms" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date de naissance</label>
              <Input name="date_naissance" value={form.date_naissance} onChange={handleChange} type="date" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Lieu de naissance</label>
              <Input name="lieu_naissance" value={form.lieu_naissance} onChange={handleChange} placeholder="Ville, Pays" />
            </div>
          </div>
        </Card>

        {/* Académique */}
        <Card>
          <div className="flex items-center gap-2 pb-3 mb-4 border-b border-gray-100 dark:border-gray-700">
            <GraduationCap size={18} className="text-blue-600" />
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">Informations académiques</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* 1. Niveau */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Niveau <span className="text-red-500">*</span></label>
              <select name="niveau" value={form.niveau} onChange={(e) => {
                setForm(f => ({ ...f, niveau: e.target.value as typeof f.niveau, classe: '', option: '', pole: '', parcours: '' }));
              }} className={selectClasses}>
                <option value="">Sélectionner un niveau</option>
                <option value="L1">Licence 1 (L1)</option>
                <option value="L2">Licence 2 (L2)</option>
                <option value="L3">Licence 3 (L3)</option>
                <option value="M1">Master 1 (M1)</option>
                <option value="M2">Master 2 (M2)</option>
              </select>
            </div>

            {/* 2. Classe (source principale — auto-remplit pôle, option, parcours) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Classe / Option <span className="text-red-500">*</span></label>
              <select name="classe" value={form.classe || ''} onChange={handleChange} className={selectClasses}>
                <option value="">Sélectionner une classe</option>
                {classesBDD
                  .filter((c: any) => !form.niveau || c.niveau === form.niveau)
                  .map((c: any) => (
                    <option key={c.id} value={c.code}>{c.code} — {c.option_nom}</option>
                  ))
                }
              </select>
              {form.classe && (
                <p className="text-[10px] text-blue-600 dark:text-blue-400 mt-0.5">
                  Le pôle, l'option et le parcours sont remplis automatiquement
                </p>
              )}
            </div>

            {/* 3. Pôle (auto-rempli, modifiable) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Pôle</label>
              <select name="pole" value={form.pole} onChange={handleChange} className={selectClasses}>
                <option value="">Sélectionner un pôle</option>
                {polesBDD.map((p: any) => (
                  <option key={p.id} value={p.nom}>{p.nom}</option>
                ))}
              </select>
            </div>

            {/* 4. Option (auto-rempli depuis la classe) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Option</label>
              <Input name="option" value={form.option} onChange={handleChange} placeholder="Option de formation" />
            </div>

            {/* 5. Parcours (auto-rempli depuis la classe) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Parcours</label>
              <Input name="parcours" value={form.parcours} onChange={handleChange} placeholder="Parcours de formation" />
            </div>

            {/* 6. Type de formation */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Type de formation <span className="text-red-500">*</span>
              </label>
              <select name="type_formation" value={form.type_formation} onChange={handleChange} className={selectClasses}>
                <option value="FI">Formation Initiale (FI)</option>
                <option value="CS">Cours du Soir (CS)</option>
                <option value="DD">Double Diplôme (DD)</option>
              </select>
            </div>
          </div>
        </Card>

        {/* Contact */}
        <Card>
          <div className="flex items-center gap-2 pb-3 mb-4 border-b border-gray-100 dark:border-gray-700">
            <Phone size={18} className="text-blue-600" />
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">Contact</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
              <Input name="email" value={form.email} onChange={handleChange} type="email" placeholder="email@exemple.com" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Téléphone</label>
              <Input name="telephone" value={form.telephone} onChange={handleChange} placeholder="+242 06 XXX XX XX" />
            </div>
          </div>
        </Card>

        {/* Photo + Statut */}
        <Card>
          <div className="flex items-center gap-2 pb-3 mb-4 border-b border-gray-100 dark:border-gray-700">
            <Camera size={18} className="text-blue-600" />
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">Photo & Statut</h3>
          </div>

          <div className="flex items-start gap-6">
            {/* Photo upload */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Photo de l'étudiant</label>
              <div className="flex items-start gap-4">
                {photoPreview && (
                  <div className="relative flex-shrink-0">
                    <img src={photoPreview} alt="Preview"
                      className="w-28 h-28 object-cover rounded-lg border border-gray-200 shadow-sm" />
                    <button type="button" onClick={handleRemovePhoto}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600 shadow">
                      <X size={14} />
                    </button>
                  </div>
                )}
                <label className="flex flex-col items-center justify-center flex-1 h-28 border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                  <Upload className="w-6 h-6 mb-1 text-gray-400" />
                  <p className="text-xs text-gray-500 text-center">
                    <span className="font-medium">Cliquer</span> pour télécharger
                  </p>
                  <p className="text-xs text-gray-400">JPG, PNG, WEBP &bull; Max 5MB</p>
                  <input ref={photoInputRef} type="file" className="hidden" accept="image/jpeg,image/png,image/webp" onChange={handlePhotoChange} />
                </label>
              </div>
            </div>

            {/* Statut */}
            <div className="w-40">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Statut</label>
              <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                <input type="checkbox" name="actif" checked={form.actif} onChange={handleChange}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500" />
                <span className={`text-sm font-medium ${form.actif ? 'text-green-700' : 'text-gray-500'}`}>
                  {form.actif ? 'Actif' : 'Inactif'}
                </span>
              </label>
            </div>
          </div>
        </Card>

        {/* Actions */}
        <div className="flex gap-3 justify-end">
          <Button type="button" variant="outline" onClick={() => navigate('/etudiants')}>
            Annuler
          </Button>
          <Button type="submit" variant="primary" disabled={saving}
            icon={saving ? <Spinner size="sm" /> : <Save size={16} />}>
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      </form>

      {/* Modal de recadrage photo (déclenchée par sélection d'un fichier) */}
      <PhotoCropModal
        open={cropSrc !== null}
        imageSrc={cropSrc || ''}
        onClose={() => setCropSrc(null)}
        onValidate={handleCropValidate}
        filename={cropFilename}
        aspect={1}
        outputSize={600}
      />
    </div>
  );
};

export default EtudiantForm;
